import { useQuery } from '@tanstack/react-query';
import { fetchSimilarTenders } from '../lib/catalog-client';
import { useT } from '../i18n/locale-context';
import { TenderList } from './tender-list';

/**
 * "Похожие тендеры" — embedding neighbours of the tender being viewed.
 *
 * Two jobs at once, which is why it is public and server-seeded:
 *
 * 1. **Crawl.** Before this, a prerendered tender page carried 235 words and
 *    **zero links to any other tender**. Only ~1,248 of 13,052 tender URLs were
 *    reachable by following links; GSC reported 9,036 "Discovered – currently
 *    not indexed" on 2026-08-01. `/results` fixed the listing side; this is the
 *    detail side.
 * 2. **Engagement.** 75% of visitors land straight on a tender page from Google
 *    and leave. This is the only thing on the page inviting a second view.
 *
 * Renders nothing when there are no neighbours — an empty "similar" heading is
 * worse than no heading. It never shows a spinner either: on the prerendered
 * page the data is already seeded, and on a client navigation an empty gap that
 * fills in silently beats a skeleton that shifts layout.
 */
export function SimilarTenders({ tenderId, locale }: { tenderId: string; locale: string }) {
  const t = useT();
  const { data } = useQuery({
    queryKey: ['catalog', 'similar', tenderId, locale],
    queryFn: () => fetchSimilarTenders(tenderId, locale),
    // Neighbours change only as the catalog turns over; refetching on every
    // focus would re-request six cards nobody asked to refresh.
    staleTime: 5 * 60 * 1000,
  });

  if (!data || data.length === 0) return null;

  return (
    <section className="mt-8" aria-labelledby="similar-tenders-heading">
      <h2 id="similar-tenders-heading" className="mb-3 text-[15px] font-semibold text-ink">
        {t('tender.similar.title')}
      </h2>
      <TenderList items={data} />
    </section>
  );
}
